import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingUp } from "lucide-react";
import { API } from "../../App";
import { formatDateSafe } from "../../lib/formatDate";

const COLORS = ["#2563eb", "#059669", "#d97706", "#db2777", "#7c3aed", "#0891b2", "#dc2626", "#65a30d"];
const fmtShort = (d) => formatDateSafe(d, { day: "2-digit", month: "2-digit" });
const fmtLong = (d) => formatDateSafe(d, { day: "numeric", month: "long", year: "numeric" });

const RatingRow = ({ goal, value, color, onRate, testId }) => (
  <div className="flex items-center gap-3 flex-wrap" data-testid={testId}>
    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
    <span className="text-sm flex-1 min-w-[180px] truncate" title={goal.goal}>{goal.goal}</span>
    <div className="flex items-center gap-0.5" role="radiogroup">
      {Array.from({ length: 11 }, (_, n) => (
        <button key={n} onClick={() => onRate(n)} className={`w-7 h-7 text-xs rounded-md border transition-colors ${value === n ? "text-white border-transparent" : "bg-background hover:bg-muted"}`} style={value === n ? { background: color } : undefined} data-testid={`${testId}-value-${n}`} aria-checked={value === n} role="radio">{n}</button>
      ))}
    </div>
  </div>
);

export const GoalProgress = ({ patientId, date, reportId, refreshKey }) => {
  const [goals, setGoals] = useState([]);
  const [ratings, setRatings] = useState([]);
  const day = (date || new Date().toISOString()).slice(0, 10);

  useEffect(() => {
    axios.get(`${API}/patients/${patientId}/goals/ratings`, { withCredentials: true }).then((r) => { setGoals(r.data.goals); setRatings(r.data.ratings); }).catch(() => {});
  }, [patientId, refreshKey]);

  const active = goals.filter((g) => g.status !== "abandonne");
  const rate = async (goal, value) => {
    try {
      const r = await axios.post(`${API}/goals/${goal.goal_id}/ratings`, { value, date: day, report_id: reportId || "" }, { withCredentials: true });
      setRatings((prev) => [...prev.filter((x) => !(x.goal_id === goal.goal_id && x.date === day)), r.data].sort((a, b) => a.date.localeCompare(b.date)));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const chart = useMemo(() => {
    const byDate = {};
    ratings.forEach((r) => { if (active.some((g) => g.goal_id === r.goal_id)) (byDate[r.date] = byDate[r.date] || { date: r.date })[r.goal_id] = r.value; });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [ratings, active]);

  if (active.length === 0) return null;
  const todayVal = (g) => ratings.find((r) => r.goal_id === g.goal_id && r.date === day)?.value;

  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid="goal-progress">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="font-medium flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Suivi des objectifs <span className="text-xs text-muted-foreground font-normal">échelle 0-10 · cotation du {fmtLong(day)}</span></h4>
        <span className="text-xs text-muted-foreground" data-testid="goal-progress-count">{ratings.length} cotation{ratings.length > 1 ? "s" : ""} · {chart.length} date{chart.length > 1 ? "s" : ""}</span>
      </div>
      <p className="text-xs text-muted-foreground">0 = objectif pas du tout atteint · 10 = objectif pleinement atteint. Une cotation par objectif et par séance ; cliquer à nouveau remplace la valeur du jour.</p>
      <div className="space-y-2">
        {active.map((g, i) => <RatingRow key={g.goal_id} goal={g} value={todayVal(g)} color={COLORS[i % COLORS.length]} onRate={(v) => rate(g, v)} testId={`goal-rating-${g.goal_id}`} />)}
      </div>
      {chart.length >= 2 ? (
        <div className="h-64" data-testid="goal-progress-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart} margin={{ top: 8, right: 16, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} />
              <XAxis dataKey="date" tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtLong} formatter={(v, k) => [v, active.find((g) => g.goal_id === k)?.goal || k]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend formatter={(k) => { const g = active.find((x) => x.goal_id === k); const t = g?.goal || k; return t.length > 40 ? t.slice(0, 40) + "…" : t; }} wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={10} stroke="#059669" strokeDasharray="4 4" opacity={0.5} />
              {active.map((g, i) => <Line key={g.goal_id} type="monotone" dataKey={g.goal_id} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 4 }} connectNulls />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic text-center py-2" data-testid="goal-progress-empty">La courbe apparaîtra dès que des cotations existent pour au moins deux dates.</p>
      )}
    </div>
  );
};
